import { useCallback, useEffect, useRef, useState } from 'react';
import { useListFilterContext, useRedirect } from 'react-admin';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

const SOURCE = 'combined_unaccent@ilike';
const DEBOUNCE_MS = 350;

/**
 * Search across the funerals list — a filter on the list, not a separate page.
 *
 * Filters as you type (debounced) rather than only on Enter, which is what the
 * sidebar's search used to require. Typing a bare case number and pressing
 * Enter still jumps straight to that case; that shortcut is well used and worth
 * keeping.
 */
export function FuneralListSearch() {
  const { filterValues, setFilters } = useListFilterContext();
  const redirect = useRedirect();

  const active = (filterValues[SOURCE] as string) ?? '';
  const [value, setValue] = useState(active);
  const timer = useRef<number>();

  // Follow the URL when the filter is cleared from outside (tab change, reset).
  useEffect(() => {
    if (!active) setValue((v) => (v === '' ? v : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const apply = useCallback(
    (next: string) => {
      const { [SOURCE]: _current, ...rest } = filterValues;
      setFilters(next ? { ...rest, [SOURCE]: next } : rest, null);
    },
    [filterValues, setFilters],
  );

  const onChange = (next: string) => {
    setValue(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => apply(next.trim()), DEBOUNCE_MS);
  };

  const clear = () => {
    window.clearTimeout(timer.current);
    setValue('');
    apply('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clear();
      return;
    }
    if (e.key !== 'Enter') return;
    const trimmed = value.trim();
    // A bare number is a case id, not a search term.
    if (/^\d+$/.test(trimmed)) {
      redirect(`/funerals/${trimmed}/show`);
      return;
    }
    window.clearTimeout(timer.current);
    apply(trimmed);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div className="relative w-56">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search name, phone, case…"
        aria-label="Search funerals"
        className="h-8 pl-8 pr-7 text-xs"
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
